import { Alert, Card, Col, Divider, Row, Skeleton } from 'antd';
import CampaignItem from 'components/Campaign/CampaignItem';
import { NumberConsumer, NumberContext } from 'contexts/legacy/NumberContext';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CampaignNumbers, Number, NumberInfo } from 'interfaces/number';
import MarketignCampaignTable, { Items } from 'components/MarketingCampaign/MarketingCampaignTable';
import { Box } from 'components/Marketing/box';
import { apiV2 } from 'services/api';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { Auths } from 'interfaces/bitrix24';
import { Campaign } from 'interfaces/marketingCampaign';
import { LanguageContext } from 'contexts/LanguageContext';
import { SyncButton } from 'components/SyncButton';
import { SystemContext } from 'contexts/legacy/SystemContext';
import { System } from 'interfaces/system';

interface MarketingCampaignViewProps {
	number: Number;
	setActiveTabKey: (key: string) => void;
	setActiveTabTutorialKey: (key: string) => void;
}

function MarketingCampaignView({
	number,
	setActiveTabKey,
	setActiveTabTutorialKey,
}: MarketingCampaignViewProps) {
	const [auths, setAuths] = useState<Auths>({ access_token: '', refresh_token: '' });
	const [domain, setDomain] = useState<string>();
	const [isUpdating, setIsUpdating] = useState(false);
	const [marketingCampaigns, setMarketingCampaigns] = useState<Campaign[]>([]);
	const [marketingCampaignsItems, setMarketingCampaignsItems] = useState<Items[]>([]);
	const [isCampaignLoading, setIsCampaignLoading] = useState(false);
	const [activeNumbers, setActiveNumbers] = useState<NumberInfo[]>([]);
	const numberContext = useContext<Number>(NumberContext);
	const systemContext = useContext<System>(SystemContext);
	const bitrix24 = useContext(Bitrix24Context);
	const { dictionary } = useContext(LanguageContext);
	const { getListCampaigns } = number;
	const headerTitles = ['numbers_form_nickname', 'number', 'total_campaign', ''];
	const headers = {
		'Access-Token': auths.access_token,
		'Refresh-Token': auths.refresh_token,
	};

	useEffect(() => {
		setActiveNumbers(numberContext.numbers.actives);
	}, [numberContext.numbers.actives]);

	useEffect(() => {
		fetchMarketingCampaigns();
	}, [domain, auths]);

	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		setAuths(bitrix24.auth);
	}, [bitrix24.auth, bitrix24.infos.domain]);

	useEffect(() => {
		setMarketingCampaignsItems(mapCampaignsToItems(marketingCampaigns));
	}, [marketingCampaigns]);

	function mapCampaignsToItems(campaigns: Campaign[]) {
		return campaigns.map((campaign) => {
			const scheduledDateToLocaleString = getScheduledTimeToLocaleString(campaign.scheduledDate);
			return {
				id: campaign.id,
				title: campaign.name,
				scheduledTime: scheduledDateToLocaleString,
				remaningTime: getRemainingTimeString(campaign),
				isOnQueue: campaign.isOnQueue,
				sent: campaign.sentMessages,
				delivered: campaign.deliveredMessages,
				read: campaign.readMessages,
				total: campaign.totalMessages,
				shouldReschedule: campaign.shouldReschedule,
				statusDescription: campaign.statusDescription,
				audiences: campaign.Audiences,
			};
		}) as Items[];
	}

	function getScheduledTimeToLocaleString(scheduledDateString: string | null) {
		if (!scheduledDateString) return dictionary.send_manually;

		const scheduledDate = new Date(scheduledDateString);
		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		};

		return new Intl.DateTimeFormat(dictionary.locale, options).format(scheduledDate);
	}

	function getRemainingTimeString(campaign: Campaign) {
		const { sentMessages, totalMessages, isOnQueue } = campaign;

		if (sentMessages === totalMessages) {
			return totalMessages === 0 ? dictionary.estimating : dictionary.finished;
		}

		if (sentMessages === 0 && totalMessages > 0) {
			return isOnQueue ? dictionary.queued : dictionary.awaiting_start;
		}

		let remainingTimeInMinutes = calculateRemainingTimeFromCampaign(campaign);
		return formatRemainingTimeInMinutesToRemainingTimeString(remainingTimeInMinutes);
	}

	function isTemplateNumberFromNumbersInfo(numberToCompare: string) {
		if (!activeNumbers) return false;
		const number = activeNumbers.find((number) => number.number === numberToCompare);
		return number && number.api === 'dialog';
	}

	function calculateRemainingTimeFromCampaign(campaign: Campaign) {
		const { sentMessages, totalMessages, originNumber } = campaign;
		const isTemplate = isTemplateNumberFromNumbersInfo(originNumber);

		const dialogRateLimitEnv = process.env.REACT_APP_DIALOG_RATE_LIMIT_PER_SECOND || '10';
		const zapiRateLimitEnv = process.env.REACT_APP_ZAPI_RATE_LIMIT_PER_MINUTE || '1';

		const dialogRateLimitPerMinute = +dialogRateLimitEnv * 60;
		const zapiRateLimitPerMinute = +zapiRateLimitEnv;

		let remainingTimeInMinutes = totalMessages - sentMessages;
		remainingTimeInMinutes /= isTemplate ? dialogRateLimitPerMinute : zapiRateLimitPerMinute;

		return Math.ceil(remainingTimeInMinutes);
	}

	function formatRemainingTimeInMinutesToRemainingTimeString(remainingTimeInMinutes: number) {
		if (remainingTimeInMinutes < 1) return dictionary.less_than_a_minute;

		const hours = Math.floor(remainingTimeInMinutes / 60);
		const minutes = remainingTimeInMinutes % 60;

		const hoursString = hours > 0 ? `${hours} ${dictionary.hour}${hours > 1 ? 's' : ''}` : '';
		const joinString = hours > 0 && minutes > 0 ? ` ${dictionary.and} ` : '';
		const minutesString =
			minutes > 0 ? `${minutes} ${dictionary.minute}${minutes > 1 ? 's' : ''}` : '';
		return hoursString + joinString + minutesString;
	}

	function fetchMarketingCampaigns() {
		setIsCampaignLoading(true);
		if (!domain) return;
		apiV2.get(`/marketing-campaign/${domain}`, { headers }).then((res) => {
			setMarketingCampaigns(res.data);
			setIsCampaignLoading(false);
		});
	}

	function getNumberCampaign(numberString: string): CampaignNumbers | false {
		const campaigns = number.campaigns.filter((campaign) => campaign.number === numberString);
		return campaigns.length > 0 ? campaigns[0] : false;
	}

	function handleClick() {
		setActiveTabKey('6');
		setActiveTabTutorialKey('5');
	}

	async function updateCampaigns() {
		setIsUpdating(true);
		await getListCampaigns();
		setIsUpdating(false);
	}

	function onStartCampaign(id: string) {
		fetchMarketingCampaigns();
	}

	const CampaignTableHeader = () => (
		<Row style={{ backgroundColor: '#e8f9fe', padding: 12, marginBottom: 1 }}>
			{headerTitles.map((column, index) => (
				<Col
					key={index}
					span={24 / headerTitles.length}
					style={{ lineHeight: '25px', fontWeight: 'bold' }}
				>
					{column !== '' && <FormattedMessage id={column} />}
				</Col>
			))}
		</Row>
	);

	const CampaignTableBody = () => (
		<>
			{number.numbers.actives.map((number) => {
				let campaign = getNumberCampaign(number.number);
				if (!campaign) {
					campaign = {
						id: 0,
						api: number.api,
						number: number.number,
						nickname: number.nickname,
						totalCampaigns: 0,
					};
				}

				return (
					<CampaignItem key={campaign.api + campaign.id} campaign={campaign} numberInfo={number} />
				);
			})}
		</>
	);

	const CampaignTable = () => (
		<Skeleton loading={number.loadingNumber}>
			{number.numbers.actives.length <= 0 ? (
				<Alert message={<FormattedMessage id="not_found_registers" />} type="warning" showIcon />
			) : (
				<>
					<CampaignTableHeader />
					<CampaignTableBody />
				</>
			)}
		</Skeleton>
	);

	const CreateCampaignsLink = (
		<a href="#" onClick={handleClick}>
			<FormattedMessage id="click_here" />
		</a>
	);

	return (
		<div className="pa-2">
			<>
				<Alert message={dictionary.status_tooltip} type="info" showIcon className="mb-2" />
				<MarketignCampaignTable
					items={marketingCampaignsItems}
					isLoading={isCampaignLoading}
					onStartCampaign={onStartCampaign}
					onSyncCampaigns={fetchMarketingCampaigns}
				/>
				<Divider />
			</>

			<Alert
				message={<FormattedMessage id="create_campaigns" values={{ link: CreateCampaignsLink }} />}
				type="warning"
				showIcon
				className="mb-2"
			/>
			<Card
				title={
					<>
						<Col span={20}>
							<FormattedMessage id="campaign_title" />
						</Col>
						<Col span={4} style={{ display: 'flex', justifyContent: 'end' }}>
							<SyncButton onClick={updateCampaigns} loading={isUpdating} />
						</Col>
					</>
				}
			>
				<CampaignTable />
			</Card>
		</div>
	);
}

export default NumberConsumer(MarketingCampaignView);
